import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatVoiceDuration } from '@/lib/groups/voiceNote';

interface VoiceNoteBubbleProps {
  src: string;
  /** Seconds, as stored by mobile in `audioDuration`. */
  duration?: number;
  isOwn: boolean;
  language: string;
}

/**
 * Playback-only bubble for voice notes recorded on mobile (web cannot record).
 * Mirrors mobile AudioBubble: play/pause button, progress line, m:ss duration.
 */
export function VoiceNoteBubble({ src, duration, isOwn, language }: VoiceNoteBubbleProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState<number | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const isArabic = language === 'ar';

  const total = duration && duration > 0 ? duration : mediaDuration;
  const progress = total ? Math.min(1, currentTime / total) : 0;

  useEffect(() => {
    // Reset when the source changes.
    setPlaying(false);
    setCurrentTime(0);
    setMediaDuration(undefined);
    setFailed(false);
  }, [src]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch (error) {
        console.error('Error playing voice note:', error);
        setFailed(true);
      }
    } else {
      audio.pause();
    }
  };

  const label = playing
    ? (isArabic ? 'إيقاف الرسالة الصوتية مؤقتاً' : 'Pause voice note')
    : (isArabic ? 'تشغيل الرسالة الصوتية' : 'Play voice note');

  return (
    <div
      dir={isArabic ? 'rtl' : 'ltr'}
      className="flex items-center gap-3 min-w-[200px]"
      role="group"
      aria-label={isArabic ? 'رسالة صوتية' : 'Voice note'}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
          if (audioRef.current) audioRef.current.currentTime = 0;
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setMediaDuration(d);
        }}
        onError={() => setFailed(true)}
      />
      <button
        type="button"
        onClick={toggle}
        disabled={failed}
        aria-label={label}
        aria-pressed={playing}
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-opacity',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isOwn ? 'bg-[#7dda55]' : 'bg-namat-text'
        )}
      >
        {playing ? (
          <Pause className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Play className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
      <div className="flex flex-1 flex-col gap-1.5">
        <div
          role="progressbar"
          aria-label={isArabic ? 'تقدم التشغيل' : 'Playback progress'}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          className={cn('h-1 w-full overflow-hidden rounded-full', isOwn ? 'bg-namat-text/20' : 'bg-black/10')}
        >
          <div
            className={cn('h-full rounded-full', isOwn ? 'bg-namat-text' : 'bg-[#7dda55]')}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <span className="text-[11px] opacity-70 tabular-nums" dir="ltr" style={{ textAlign: isArabic ? 'right' : 'left' }}>
          {failed
            ? (isArabic ? 'تعذر تشغيل الرسالة الصوتية' : 'Voice note unavailable')
            : playing || currentTime > 0
              ? `${formatVoiceDuration(currentTime)} / ${formatVoiceDuration(total)}`
              : formatVoiceDuration(total)}
        </span>
      </div>
    </div>
  );
}
